// Modelo freemium de MiPAE — fuente única de verdad del gating.
//
// Desde el 25-oct se venden tres membresías, todas mensuales con renovación
// automática (decisiones de Sebastián del 17-oct): Estudiante Plus $7.990,
// Enfermero Plus $10.990 y Full $13.990 (productosPlus.js). El valor interno
// premium se conserva para no invalidar membresías existentes.
//
// La matemática pura del gating (cuotas, trial, lanzamiento abierto) vive en
// `planGating.js` — sin ese import, `api/claude.js` (Edge Function) no podría
// reusarla para validar la cuota en el servidor. Se re-exporta completa acá
// para que ningún consumidor existente tenga que cambiar su import.
import { supabase } from './supabase';
import { PRODUCTOS_PLUS, MEMBRESIA_GENERICA, PRECIOS_ANUNCIADOS_CLP, precioClp } from './productosPlus';
import { CUOTA_PLUS, CUOTA_EVOLUCION_SEMANA_PLUS, diasTrialPara } from './planGating';
export * from './planGating';

// Los límites razonables de la membresía dichos con su número, en UN lugar:
// las caras, Tu acceso, la tabla comparativa y los correos los leen de aquí
// (los mismos de /precios, fijado por test).
export const BENEFICIO_BENITO = `Hasta ${CUOTA_PLUS.benitoDia} consultas al día a Benito, con un máximo de ${CUOTA_PLUS.benitoMes} al mes`;
export const BENEFICIO_EVOLUCION = `Hasta ${CUOTA_EVOLUCION_SEMANA_PLUS} evoluciones con IA por semana en MiPAE en turno`;

// Nombre comercial del tier que se vende hoy (de cara al usuario): Estudiante
// Plus, el único con contratación preparada. Sale del catálogo de productos,
// así que un rename aplica en TODAS las superficies. Para nombrar la
// membresía de una persona, nombreMembresia(profile) de productosPlus.
export const TIER_NOMBRE = PRODUCTOS_PLUS.plus_estudiante.nombre;

export const PRECIOS = {
  mensual: { clp: 7990, label: '$7.990', periodo: 'al mes' },
  // Semestral a $19.990 desde la extendida (D5, 2026-09-14): el estudiante
  // piensa en semestres. Tres encarnaciones a mantener iguales: esta,
  // PLAN_CONFIG en api/flow-checkout.js y PLAN_PRECIO en api/_shared/flow.js
  // (fijado por test en plan.test.js).
  semestral: { clp: 19990, label: '$19.990', periodo: '6 meses', porMes: '≈ $3.330/mes' },
};

// Inicia el checkout de Flow para el plan dado ('mensual' | 'semestral').
// Requiere sesión activa. Redirige al usuario a la página de pago de Flow.
// `modoEsperado` ('unico' | 'recurrente') es el modo cuyas condiciones la
// pantalla mostró: el servidor rechaza con 409 si haría otra cosa, así nadie
// paga una renovación habiendo leído "pago único" (ni al revés). `producto`
// es la membresía elegida (desde el 25-oct se venden tres) y el monto que
// viaja es el de ESA membresía: el servidor rechaza si no coincide.
export async function iniciarCheckout(planType, modoEsperado = null, producto = 'plus_estudiante', prueba = null) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sesión requerida');

  const res = await fetch('/api/flow-checkout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ planType, producto, modoEsperado: modoEsperado || 'unico',
      ...(prueba ? { prueba } : {}),
      montoEsperado: planType === 'mensual' ? PRECIOS_ANUNCIADOS_CLP[producto] : PRECIOS[planType]?.clp }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error al iniciar pago');
  return data.url;
}

// ¿Qué membresía se puede contratar? Sonda GET sin sesión a
// /api/flow-checkout (solo booleanos): `productos[id].recurrente` dice si
// esa membresía tiene su plan mensual configurado. Sin plan no se contrata
// (no hay pago único de respaldo). Si la sonda falla devuelve null y la
// pantalla no deja pagar. Un servidor anterior que no trae `productos` solo
// conoce Estudiante Plus.
export async function obtenerModoCheckout() {
  try {
    const res = await fetch('/api/flow-checkout', { method: 'GET' });
    if (!res.ok) return null;
    const data = await res.json();
    const r = data?.recurrente;
    if (!r || typeof r !== 'object') return null;
    const productos = Object.fromEntries(Object.keys(PRODUCTOS_PLUS).map((id) => {
      const p = data.productos?.[id];
      const recurrente = p ? p.recurrente === true : id === 'plus_estudiante' && r.mensual === true;
      return [id, { recurrente }];
    }));
    return { pruebaConTarjeta: data.pruebaConTarjeta === true, diasPrueba: data.diasPrueba || {}, mensual: r.mensual === true, semestral: r.semestral === true, productos,
      ...(data.checkoutEnabled === false ? { checkoutEnabled: false } : {}) };
  } catch {
    return null;
  }
}

// Las condiciones que se muestran ANTES del botón de pago (Términos §6: se
// informan al contratar). Una sola fuente para la card de aporte y la de
// venta, y las mismas palabras que el correo de activación
// (api/_shared/flow.js, textoRenovacion). Sin guiones largos.
export function condicionesPlan(planType = 'mensual', recurrente = false, producto = 'plus_estudiante') {
  const precio = planType === 'mensual' && PRECIOS_ANUNCIADOS_CLP[producto]
    ? precioClp(PRECIOS_ANUNCIADOS_CLP[producto])
    : PRECIOS[planType]?.label ?? PRECIOS.mensual.label;
  const nombre = PRODUCTOS_PLUS[producto]?.nombre ?? TIER_NOMBRE;
  const retracto = 'Tienes 10 días corridos para retractarte escribiendo a contacto@mipae.cl.';
  if (recurrente) {
    const cada = planType === 'semestral' ? 'cada 6 meses' : 'cada mes';
    return `Se renueva sola ${cada} por ${precio} (IVA incluido) en la tarjeta que registres. Puedes cancelar la renovación cuando quieras desde Mi perfil y sigues con acceso hasta el fin del período ya pagado. ${retracto}`;
  }
  const dias = planType === 'semestral' ? '6 meses' : '31 días';
  return `Es un pago único de ${precio} (IVA incluido) que deja ${nombre} vigente por ${dias}. No se renueva sola ni se vuelve a cobrar, y ${retracto.charAt(0).toLowerCase()}${retracto.slice(1)}`;
}

// La fila de `subscriptions` del usuario (RLS: solo lee la suya). Null si no
// hay sesión, no hay fila o falla: Mi perfil entonces no afirma nada sobre
// la renovación.
export async function obtenerMiSuscripcion() {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('modo, status, plan_type, period_end, cancel_at_period_end, morosa, trial_days, trial_starts_at, trial_ends_at')
      .maybeSingle();
    if (error || !data) return null;
    return data;
  } catch {
    return null;
  }
}

// Cancela la renovación automática (al final del período pagado). Requiere
// sesión. Devuelve la fecha hasta la que sigue el acceso, o lanza.
export async function cancelarRenovacion() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sesión requerida');
  const res = await fetch('/api/flow-cancelar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'No se pudo cancelar la renovación');
  return data.accesoHasta ? new Date(data.accesoHasta) : null;
}

// Enciende el trial vía RPC. Devuelve la fecha de término (Date) o lanza.
export async function iniciarTrial() {
  // Compatibilidad con entradas antiguas: nunca activa sin pasar por las
  // condiciones y el registro de tarjeta. En nativo no se ofrece esta acción.
  window.location.assign('/?planes=1');
  return null;
}

// Lee el uso de IA del mes del usuario autenticado (RPC mi_uso_ia_mes).
// Devuelve { paes, detallados, revisas } o null si falla / no hay sesión.
export async function obtenerUsoMes() {
  try {
    const { data, error } = await supabase.rpc('mi_uso_ia_mes');
    if (error || !data) return null;
    // La función devuelve una fila (table). supabase-js la entrega como array.
    const row = Array.isArray(data) ? data[0] : data;
    return { paes: row?.paes ?? 0, detallados: row?.detallados ?? 0, revisas: row?.revisas ?? 0 };
  } catch {
    return null;
  }
}

// Llamadas de Benito del día (hora de Chile), RPC mi_uso_benito_hoy.
// Fail-open a null: sin dato, puedeUsarIA('benito') no bloquea.
export async function obtenerUsoBenitoHoy() {
  try {
    const { data, error } = await supabase.rpc('mi_uso_benito_hoy');
    if (error || data === null || data === undefined) return null;
    const n = Number(data);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

// Consultas del mes calendario en Chile, por cuenta. El primer período
// empieza el 25-oct: lo usado durante el acceso abierto no consume este cupo.
export async function obtenerUsoBenitoMes() {
  try {
    const { data, error } = await supabase.rpc('mi_uso_benito_mes');
    if (error || data == null) return null;
    const n = Number(data);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

// Uso del mes de "PAE con tu pauta" (RPC mi_uso_pauta_mes → integer).
// Devuelve el número o null si falla / no hay sesión (fail-open).
export async function obtenerUsoPautaMes() {
  try {
    const { data, error } = await supabase.rpc('mi_uso_pauta_mes');
    if (error || data == null) return null;
    const n = Array.isArray(data) ? data[0] : data;
    return Number.isFinite(Number(n)) ? Number(n) : null;
  } catch {
    return null;
  }
}

// ── Las caras de MiPAE Estudiante ────────────────────────────────────────────
// Un solo plan, presentación por rol (profiles.rol). El paywall lee el rol y
// muestra la propuesta de valor de esa capa: mismo producto, gancho distinto.
// Desde el 2026-10-04 hay tres roles (lib/roles.js): estudiante y enfermero
// tienen su cara; TENS y sin rol ven la cara `sinRol`.
//
// Cada cara: { id, gancho (titular), bajada, beneficios[], invitaPerfil? }.
// 'invitaPerfil' = la cara sin rol; en vez de cerrar la venta, invita a
// completar el perfil (no sabemos aún qué capa monetizar).
//
// Solo se promete lo que DE VERDAD separa a los planes (D8, 2026-09-14): IA
// sin tope y con Sonnet en Benito, memoria de ramos, mis procedimientos, PDF
// de Benito y PAE con tu pauta sin tope. Ningún módulo es de pago.
// Los beneficios dicen lo MISMO que la página de precios (public/precios,
// revisada por el fundador el 16-oct): el límite razonable con su número
// (BENEFICIO_BENITO, BENEFICIO_EVOLUCION) y nunca "sin tope", porque las
// funciones con IA tienen límites de uso en todos los planes. La enfermera ya no ve el PAE en su
// experiencia (decisión del 16-oct), así que su cara no lo promete.
export const CARAS = {
  // MiPAE Estudiante (Estudiante Plus).
  estudiante: {
    id: 'estudiante',
    nombre: PRODUCTOS_PLUS.plus_estudiante.nombre,
    producto: 'plus_estudiante',
    // Gancho y bajada venden el resultado, no "más IA" (auditoría de
    // conversión del 25-oct-2026): para qué le sirve a quien estudia.
    gancho: 'Arma todos los PAE que te pidan tus ramos',
    bajada:
      'Para las semanas de entregas, cuando un PAE al mes no alcanza. Rápido, Detallado y con tu pauta sin cuota mensual, y Benito para entender cada paso de tu caso.',
    beneficios: [
      'PAE sin cuota mensual',
      BENEFICIO_BENITO,
      'Benito recuerda tus ramos',
    ],
  },
  // MiPAE Enfermero (Enfermero Plus).
  enfermero: {
    id: 'enfermero',
    nombre: PRODUCTOS_PLUS.plus_enfermero.nombre,
    producto: 'plus_enfermero',
    // "Más IA para tu turno" vendía la herramienta; esto dice lo que la
    // enfermera obtiene, con las palabras de /precios (auditoría del 25-oct).
    gancho: 'Tu evolución ordenada, turno tras turno',
    bajada:
      'Para cuando una evolución con IA por semana no alcanza para tus turnos. Lo que registras se organiza en la evolución por sistemas de cada paciente y tú la revisas antes de copiarla.',
    beneficios: [
      BENEFICIO_EVOLUCION,
      BENEFICIO_BENITO,
      'Hasta 20 procedimientos propios',
    ],
  },
  // Sin rol: vitrina + invitación a completar perfil.
  sinRol: {
    id: 'sinRol',
    nombre: MEMBRESIA_GENERICA,
    producto: null,
    gancho: 'Tu compañero, en cada etapa',
    bajada:
      'Completa tu perfil y te mostramos lo que suma una membresía en tu experiencia. MiPAE TENS no necesita membresía.',
    beneficios: [
      BENEFICIO_BENITO,
      'Las respuestas de Benito en PDF, en la web',
      'La enciclopedia completa',
    ],
    invitaPerfil: true,
  },
};

// ¿Se puede contratar la membresía de esta cara? Solo la que tiene
// contratación construida (`disponible` en productosPlus). Desde el 17-oct
// son las tres; a una enfermera nunca se le ofrece Estudiante Plus como si
// fuera su membresía, y la cara sin rol (TENS incluido) no vende.
export function caraContratable(cara) {
  return PRODUCTOS_PLUS[cara?.producto]?.disponible === true;
}

// Lo que se le ofrece a cada experiencia, en este orden: su Plus y Full, como
// la página de precios ("¿Ejerces y también estudias, o estás en tu
// internado? Full reúne Estudiante Plus y Enfermero Plus"). MiPAE TENS no
// necesita membresía, y sin rol no se vende. Con una membresía vigente solo
// se ofrece renovar ESA: el servidor rechaza otra distinta (409), porque el
// cambio entre membresías no está decidido. Lo decide el servidor también
// (puedeContratar y otraMembresiaVigente en api/).
export function ofertaPara(profile, ahora = Date.now()) {
  const cara = caraMipaePlus(profile);
  if (!caraContratable(cara)) return [];
  const lista = [cara.producto, 'full'].filter((p) => PRODUCTOS_PLUS[p]?.disponible === true);
  const vigente = membresiaVigente(profile, ahora);
  return vigente ? lista.filter((p) => p === vigente) : lista;
}

// El producto de la membresía pagada vigente, o null (permiso antiguo sin
// producto, vencida o cuenta gratis). Misma regla que otraMembresiaVigente.
export function membresiaVigente(profile, ahora = Date.now()) {
  if (profile?.plan !== 'premium' || !PRODUCTOS_PLUS[profile?.premium_product]) return null;
  if (profile.premium_until && new Date(profile.premium_until).getTime() <= ahora) return null;
  return profile.premium_product;
}

// La línea bajo cada opción, con las palabras de /precios. La de Full dice lo
// que SUMA al Plus de tu experiencia, que es lo que hay que decidir.
export function lineaOpcion(producto, profile) {
  if (producto === 'plus_estudiante') return 'PAE sin cuota, más Benito y tus ramos';
  if (producto === 'plus_enfermero') return 'Más evoluciones con IA y más Benito';
  return profile?.rol === 'enfermero'
    ? 'Suma Estudiante Plus: PAE sin cuota y la memoria de tus ramos'
    : 'Suma Enfermero Plus: más evoluciones con IA en MiPAE en turno';
}

// Devuelve la cara de MiPAE Estudiante para un perfil dado, leyendo profiles.rol.
// Estudiante y enfermero tienen la suya; cualquier otro valor (TENS, sin rol)
// cae en `sinRol`. Docente, postgrado y tutor ya no existen como rol desde el
// 2026-10-04: la BD los traduce a enfermero (lib/roles.js).
export function caraMipaePlus(profile) {
  const rol = profile?.rol;
  // hasOwn para que un rol con nombre de propiedad heredada no devuelva basura.
  return Object.prototype.hasOwnProperty.call(CARAS, rol) ? CARAS[rol] : CARAS.sinRol;
}

// La duración del botón corresponde al rol real de la cuenta.
export const CTA_BASE = `Activar ${TIER_NOMBRE}`;
export const CTA_TRIAL = 'Probar 7 días gratis';

export function ctaPaywall(trialDisponible = false, profile = null) {
  const dias = diasTrialPara(profile) || 7;
  return trialDisponible ? `Probar ${dias} días gratis` : CTA_BASE;
}


// Reconciliación autenticada al volver del pago. Sin sesión no se afirma éxito.
export async function verificarMiPago() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return null;
  const res = await fetch('/api/flow-estado', {
    method: 'POST', headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!res.ok) return null;
  return res.json();
}

